import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useSeoProperties from '@aurora/shared-client/components/seo/useSeoProperties';
import useIsomorphicRedirect, {
  RedirectStatusCode
} from '@aurora/shared-client/components/useIsomorphicRedirect';
import type { MessagePagesAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import type { RouteWithQuery } from '@aurora/shared-client/routes/useCustomRouter';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import {
  EndUserPages,
  EndUserPathParams,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import { stripHtmlFromString } from '@aurora/shared-utils/helpers/objects/StringHelper';
import { canUseDOM } from 'exenv';
import type { ArticleJsonLdProps } from 'next-seo';
import { ArticleJsonLd } from 'next-seo';
import React, { useContext } from 'react';
import {
  UrlObject,
  useContextObjectRefFromUrl
} from '../../../components/context/useContextObjectFromUrl';
import MessagePage from '../../../components/page/MessagePage/MessagePage';
import { contextMessageToMessageView } from '../../../helpers/messages/MessageHelper/MessageHelper';
import { buildMessageUrl } from '../../../helpers/seo/TkbSchemaHelper';

/**
 * TKB Message Page
 *
 * @author Aditi Agarwal
 */
const TkbMessagePage: React.FC<React.PropsWithChildren> = () => {
  const { contextMessage } = useContext(AppContext);
  const { baseUrl } = useContext(TenantContext);
  const { router } = useEndUserRoutes();
  const { customOGSiteName, getCaseSensitivePath } = useSeoProperties();
  const { id: replyId } = useContextObjectRefFromUrl(UrlObject.REPLY);
  const isPermalinkPage = replyId !== null;

  const nodeId = router.getUnwrappedQueryParam(EndUserQueryParams.NODE_ID);
  const articleId = router.getUnwrappedQueryParam(EndUserQueryParams.ARTICLE_ID);
  const shouldRedirect = !canUseDOM && !!(nodeId && articleId);

  function redirectRoute(): RouteWithQuery<EndUserPages, EndUserQueryParams> {
    const guideId = router.getUnwrappedQueryParam(EndUserQueryParams.GUIDE_ID);
    const showManualSort = router.getUnwrappedQueryParam(EndUserQueryParams.SHOW_MANUAL_SORT_NAV);
    return {
      route: EndUserPages.TkbMessagePage,
      params: {
        ...router.getPathParams<MessagePagesAndParams>(),
        [EndUserPathParams.BOARD_ID]: nodeId?.split(':')[1],
        [EndUserPathParams.MESSAGE_ID]: articleId?.split(':')[1]
      },
      query: {
        [EndUserQueryParams.GUIDE_ID]: guideId,
        [EndUserQueryParams.SHOW_MANUAL_SORT_NAV]: showManualSort
      }
    };
  }

  /**
   * On the server side, it checks for the query parameters,
   * and if they exist, it updates the tkb message page route.
   */
  useIsomorphicRedirect(shouldRedirect, redirectRoute(), RedirectStatusCode.MOVED_PERMANENTLY);

  function renderArticleSchema(): React.ReactElement {
    const {
      author: { login, avatar },
      subject,
      body,
      postTime,
      currentRevision: { lastEditTime },
      images: { edges: imageEdges }
    } = contextMessage;

    const articleJsonLd: ArticleJsonLdProps = {
      url: buildMessageUrl(
        contextMessageToMessageView(contextMessage),
        router,
        baseUrl,
        getCaseSensitivePath
      ),
      title: stripHtmlFromString(subject),
      description: stripHtmlFromString(body),
      images: imageEdges.length > 0 ? [imageEdges[0].node.url] : [],
      datePublished: new Date(postTime).toLocaleString(),
      dateModified: new Date(lastEditTime).toLocaleString(),
      publisherName: customOGSiteName || '',
      authorName: !avatar
        ? login
        : {
            name: login,
            url: avatar.url
          }
    };

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <ArticleJsonLd {...articleJsonLd} />;
  }

  return (
    <>
      <MessagePage pageId={EndUserPages.TkbMessagePage} />
      {contextMessage && !isPermalinkPage && renderArticleSchema()}
    </>
  );
};
export default TkbMessagePage;
